import api from '@/api/client';
import { servicePath } from '@/config/env';
import { getInitials } from '@/lib/getInitials';
import { ACCOUNT_TYPE_LABELS } from '@/features/profile/constants';
import { buildLayoutFromBoardWithCategory } from '@/features/profile/classes/profileClassesLayout';
import type { ProfileClassOrganizationLayout } from '@/features/profile/classes/classCategoryTypes';
import { getAccountTypeFromUserTypeId, getUserTypeLabel } from '@/features/profile/userType';
import { registerBoard } from '@/features/class/classRegistry';
import type {
  FollowStatus,
  OwnedClassCard,
  ProfileConnection,
  ProfileJoinedClassListItem,
  ProfilePostItem,
  ProfileStats,
  UserAccountType,
  UserProfile,
} from '@/features/profile/types';

export interface ProfileResponse {
  profile: UserProfile;
  ownedClasses: OwnedClassCard[];
  posts: ProfilePostItem[];
}

export async function fetchCurrentUserProfile(): Promise<ProfileResponse> {
  const { data } = await api.get<ProfileResponse>('/users/me/profile');
  return data;
}

// ---------------------------------------------------------------------------
// GET /userservice/api/v3/user/:userId
// ---------------------------------------------------------------------------

function pickString(source: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
  }
  return undefined;
}

function pickNumber(source: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
}

function pickBool(source: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (value === 'true' || value === 1) return true;
    if (value === 'false' || value === 0) return false;
  }
  return false;
}

/**
 * Extract a usable image URL from one of MyU's media descriptor objects, e.g.
 *   { mediaPath, compressedMediaPath, thumbnailPath, ... }
 * Falls back to a plain string field if the value is already a URL.
 */
function pickMediaUrl(source: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (!value) continue;
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'object') {
      const media = value as Record<string, unknown>;
      const url =
        (typeof media.mediaPath === 'string' && media.mediaPath) ||
        (typeof media.compressedMediaPath === 'string' && media.compressedMediaPath) ||
        (typeof media.thumbnailPath === 'string' && media.thumbnailPath) ||
        (typeof media.url === 'string' && media.url) ||
        (typeof media.path === 'string' && media.path);
      if (url) return url;
    }
  }
  return undefined;
}

function resolveAccountType(source: Record<string, unknown>): UserAccountType {
  // Prefer the numeric `userTypeId` when present — it's the most reliable signal.
  const numericId = pickNumber(source, 'userTypeId', 'user_type_id', 'accountTypeId');
  const fromId = getAccountTypeFromUserTypeId(numericId);
  if (fromId) return fromId;

  const raw = (
    pickString(source, 'accountType', 'userType', 'role', 'type') ?? 'student'
  ).toLowerCase();
  if (
    raw.includes('teach') ||
    raw.includes('instructor') ||
    raw.includes('professor') ||
    raw.includes('faculty')
  ) {
    return 'instructor';
  }
  if (raw.includes('promoter')) return 'promoter';
  if (raw.includes('tutor')) return 'tutor';
  if (raw.includes('parent') || raw.includes('guardian')) return 'parent';
  if (
    raw.includes('club') ||
    raw.includes('manage') ||
    raw.includes('admin') ||
    raw.includes('staff') ||
    raw.includes('department')
  ) {
    return 'management';
  }
  return 'student';
}

function normalizeFollowStatus(source: Record<string, unknown>): FollowStatus | undefined {
  if (
    !('isfollow' in source) &&
    !('isRequest' in source) &&
    !('isFollow' in source) &&
    !('is_follow' in source) &&
    !('followStatus' in source)
  ) {
    return undefined;
  }

  const rawStatus = pickString(source, 'followStatus');
  if (rawStatus) {
    const normalized = rawStatus.toLowerCase();
    if (normalized === 'following') return 'following';
    if (normalized === 'requested' || normalized === 'request') return 'requested';
    if (normalized === 'none' || normalized === 'follow') return 'none';
  }

  const code = pickNumber(source, 'isfollow', 'isRequest', 'isFollow', 'is_follow');
  if (code === 2) return 'following';
  if (code === 1) return 'requested';
  return 'none';
}

function extractRawUserPayload(payload: unknown): Record<string, unknown> {
  const root = (payload ?? {}) as Record<string, unknown>;
  const data = (root.data ?? root.result ?? root) as Record<string, unknown>;
  const user = (data.user ?? root.user) as Record<string, unknown> | undefined;
  return user && typeof user === 'object' ? { ...data, ...user } : data;
}

/** Normalize the raw `/userservice/api/v3/user/:id` payload into our `UserProfile` shape. */
export function normalizeUserProfile(payload: unknown): UserProfile {
  const data = extractRawUserPayload(payload);

  const id = pickString(data, 'id', 'userId', '_id', 'uuid') ?? '';

  // MyU backend uses `name` for first name, and `surName` (or `lastName`) for last name.
  const firstName = pickString(data, 'firstName', 'first_name') ?? pickString(data, 'name') ?? '';
  const lastName = pickString(data, 'surName', 'lastName', 'last_name', 'surname') ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const username = pickString(data, 'username', 'userName', 'handle') ?? '';

  // Prefer "name + surName" → fall back to fullName/displayName, then first name or username.
  const displayName =
    fullName || pickString(data, 'fullName', 'displayName') || firstName || username || 'User';

  const email = pickString(data, 'email', 'emailAddress') ?? '';
  const mobileNo = pickString(data, 'mobileNo', 'mobileNumber', 'phone', 'phoneNumber');

  // Avatar — supports both plain URL strings and the nested media-object shape
  // (`profileImage: { mediaPath, compressedMediaPath, thumbnailPath }`).
  const avatarUrl = pickMediaUrl(
    data,
    'profileImageThumbnail',
    'profileImage',
    'avatarUrl',
    'profilePic',
    'photo',
    'profilePicture',
    'imageUrl',
    'image',
  );

  // Cover / banner — same nested-media handling for `backgroundImage`.
  const bannerUrl = pickMediaUrl(
    data,
    'backgroundImage',
    'bannerUrl',
    'coverImage',
    'coverPhoto',
    'coverPicture',
    'coverImageUrl',
    'coverPictureUrl',
    'profileCoverImage',
    'profileBackground',
    'profileBackgroundImage',
    'backgroundPicture',
    'backImage',
    'backgroundUrl',
  );

  // Institution — MyU returns this as a nested `university.name` object.
  const universityObj = (data.university as Record<string, unknown> | undefined) ?? {};
  const institution =
    pickString(universityObj, 'name', 'abbreviation') ??
    pickString(data, 'schoolName', 'school_name', 'institution', 'organisation') ??
    '';

  const departmentObj = (data.department as Record<string, unknown> | undefined) ?? {};
  const department =
    pickString(departmentObj, 'name') ?? pickString(data, 'departmentName', 'department') ?? '';

  const graduationYear =
    pickString(data, 'graduationYear', 'graduation_year', 'classOf') ??
    (pickNumber(data, 'graduationYear', 'graduation_year') || undefined);

  const bio = pickString(data, 'bio', 'aboutMe', 'description');

  const verified = pickBool(data, 'userVerified', 'isVerified', 'verified');
  const isPremium = pickBool(data, 'isPremiumUser', 'isPremium', 'premium', 'premiumUser');
  const isPrivate = pickBool(data, 'isPrivate');
  const followStatus = normalizeFollowStatus(data);

  const accountType = resolveAccountType(data);
  const accountTypeLabel =
    pickString(data, 'accountTypeLabel', 'roleLabel') ?? ACCOUNT_TYPE_LABELS[accountType];

  // MyU backend stats live at the top level: newsCount, joinedBoardCount, followerCount, followingCount.
  const statsSource = (data.stats as Record<string, unknown> | undefined) ?? data;
  const stats: ProfileStats = {
    posts: pickNumber(statsSource, 'newsCount', 'postCount', 'posts', 'totalPosts'),
    following: pickNumber(statsSource, 'followingCount', 'following', 'totalFollowing'),
    followers: pickNumber(statsSource, 'followerCount', 'followers', 'totalFollowers'),
    joinedClasses: pickNumber(
      statsSource,
      'joinedBoardCount',
      'joinedClassCount',
      'joinedClasses',
      'classCount',
    ),
  };

  return {
    id,
    name: displayName,
    username,
    email,
    accountType,
    accountTypeLabel,
    institution,
    bio,
    department,
    graduationYear,
    initials: getInitials(displayName),
    avatarUrl,
    bannerUrl,
    verified,
    isPremium,
    isPrivate,
    followStatus,
    mobileNo,
    stats,
    storage: normalizeStorageInfo(data),
    prime: normalizePrimeInfo(data, isPremium),
    notifications: normalizeNotificationPrefs(data),
    privacy: normalizePrivacyPrefs(data),
  };
}

function normalizeNotificationPrefs(data: Record<string, unknown>) {
  const has =
    'receiveNewPostNotification' in data ||
    'receiveLikeNotification' in data ||
    'receiveCommentNotification' in data ||
    'receivePrivateMsgNotification' in data ||
    'receiveFavoriteFollowNotification' in data ||
    'receiveJoinBoardNotification' in data;
  if (!has) return undefined;
  return {
    newPosts: pickBool(data, 'receiveNewPostNotification'),
    likes: pickBool(data, 'receiveLikeNotification'),
    comments: pickBool(data, 'receiveCommentNotification'),
    messages: pickBool(data, 'receivePrivateMsgNotification'),
    following: pickBool(data, 'receiveFavoriteFollowNotification'),
    classJoins: pickBool(data, 'receiveJoinBoardNotification'),
  };
}

/**
 * Map `receivePrivateMsgSetting` (server enum 1–4) → `MessagePrivacyOption`.
 *   1 → anyone on MyU
 *   2 → within my school + accounts I follow / share a class with
 *   3 → only accounts I follow / share a class with
 *   4 → no one
 */
const MESSAGE_FROM_BY_CODE: Record<
  number,
  'anyone' | 'school_and_connections' | 'connections_only' | 'no_one'
> = {
  1: 'anyone',
  2: 'school_and_connections',
  3: 'connections_only',
  4: 'no_one',
};

function normalizePrivacyPrefs(data: Record<string, unknown>) {
  const has =
    'isPrivate' in data ||
    'allowInPopularList' in data ||
    'receiveComment' in data ||
    'allowDownloads' in data ||
    'receivePrivateMsgSetting' in data;
  if (!has) return undefined;
  const code = pickNumber(data, 'receivePrivateMsgSetting');
  const messageFrom = MESSAGE_FROM_BY_CODE[code] ?? 'anyone';
  return {
    accountPrivate: pickBool(data, 'isPrivate'),
    showOnExplore: pickBool(data, 'allowInPopularList'),
    receiveComments: pickBool(data, 'receiveComment'),
    allowDownloads: !('allowDownloads' in data) || pickBool(data, 'allowDownloads'),
    messageFrom,
  };
}

function normalizeStorageInfo(data: Record<string, unknown>) {
  const usedBytes = pickNumber(data, 'consumedSpace', 'usedSpace', 'storageUsedBytes');
  const totalBytes = pickNumber(data, 'totalAllocatedSpace', 'allocatedSpace', 'storageLimitBytes');
  if (!usedBytes && !totalBytes) return undefined;
  return { usedBytes, totalBytes };
}

function normalizePrimeInfo(data: Record<string, unknown>, isPremium: boolean) {
  const pkg = (data.packageInfo as Record<string, unknown> | undefined) ?? undefined;
  if (!pkg && !isPremium) return undefined;
  const expiresAt = pkg ? pickNumber(pkg, 'expiryDate', 'expiresAt') || undefined : undefined;
  const planName = pkg ? pickString(pkg, 'name', 'planName') : undefined;
  return {
    isActive: Boolean(isPremium) || Boolean(expiresAt && expiresAt > Date.now()),
    planName,
    expiresAt,
  };
}

/** GET /userservice/api/v3/user/:userId — returns the normalized `UserProfile`. */
export async function fetchUserById(userId: string | number): Promise<UserProfile> {
  const { data } = await api.get<unknown>(servicePath('user', `user/${userId}`));
  return normalizeUserProfile(data);
}

export interface UserProfileBundle {
  readonly profile: UserProfile;
  readonly classesLayout: ProfileClassOrganizationLayout;
  readonly raw: Record<string, unknown>;
}

/** Full profile payload including owned classes from `boardWithCategory`. */
export async function fetchUserProfileBundle(userId: string | number): Promise<UserProfileBundle> {
  const { data } = await api.get<unknown>(servicePath('user', `user/${userId}`));
  const raw = extractRawUserPayload(data);
  return {
    profile: normalizeUserProfile(data),
    classesLayout: buildLayoutFromBoardWithCategory(raw.boardWithCategory),
    raw,
  };
}

export interface ToggleUserFollowParams {
  followedUserId: number | string;
  followerUserId: number | string;
  followToggle: boolean;
}

export interface ToggleUserFollowResult {
  followStatus: FollowStatus;
}

/**
 * POST /userservice/api/v3/user/follow
 *
 * Toggle follow / unfollow / cancel request for another user.
 */
export async function toggleUserFollow(
  params: ToggleUserFollowParams,
): Promise<ToggleUserFollowResult> {
  const { data } = await api.post<unknown>(servicePath('user', 'user/follow'), {
    followedUserId: Number(params.followedUserId),
    followerUserId: Number(params.followerUserId),
    followToggle: params.followToggle,
  });

  const root = (data ?? {}) as Record<string, unknown>;
  const followed = (root.followed ?? root.data ?? root) as Record<string, unknown>;
  const statusSource =
    typeof followed === 'object' &&
    followed != null &&
    ('isfollow' in followed ||
      'isRequest' in followed ||
      'isFollow' in followed ||
      'is_follow' in followed ||
      'followStatus' in followed)
      ? followed
      : root;

  const parsedStatus = normalizeFollowStatus(statusSource as Record<string, unknown>);
  const followStatus = params.followToggle ? (parsedStatus ?? 'following') : 'none';
  return { followStatus };
}

// ---------------------------------------------------------------------------
// PUT /userservice/api/v3/user/:userId
// ---------------------------------------------------------------------------

/** Payload accepted by the user-service profile update endpoint. */
export interface UpdateUserProfilePayload {
  name?: string;
  surName?: string;
  username?: string;
  email?: string;
  mobileNo?: string;
  bio?: string;
  gender?: 'Male' | 'Female' | null;
  dateOfBirth?: string;
  /** Pass-through for any extra backend fields we don't model yet. */
  [key: string]: unknown;
}

/**
 * PUT /userservice/api/v3/user/:userId — update the user's profile.
 * Returns the normalized profile parsed from the response.
 */
export async function updateUserProfile(
  userId: string | number,
  payload: UpdateUserProfilePayload,
): Promise<UserProfile> {
  const sanitizedPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );
  const options = {
    meta: { suppressGlobalErrorToast: true },
  } as never;

  const { data } = await api.patch<unknown>(
    servicePath('user', `user/${userId}`),
    sanitizedPayload,
    options,
  );
  return normalizeUserProfile(data);
}

/**
 * GET /userservice/api/v3/user/:userId — returns both the normalized profile
 * **and** the raw payload (used to refresh `authStore.rawUser` so counts like
 * `newsCount` / `followingCount` / `followerCount` / `joinedBoardCount` stay
 * in sync between app sessions).
 */
export async function fetchUserByIdWithRaw(
  userId: string | number,
): Promise<{ profile: UserProfile; raw: Record<string, unknown> }> {
  const { data } = await api.get<unknown>(servicePath('user', `user/${userId}`));
  const raw = extractRawUserPayload(data);
  return { profile: normalizeUserProfile(data), raw };
}

// ---------------------------------------------------------------------------
// GET /userservice/api/v3/user/follow      (following list)
// GET /userservice/api/v3/user/followedBy  (followers list)
// ---------------------------------------------------------------------------

export interface ProfileConnectionPage {
  readonly items: readonly ProfileConnection[];
  readonly totalCount: number;
}

function normalizeConnectionUser(raw: Record<string, unknown>): ProfileConnection {
  const id = pickString(raw, 'id', 'userId', '_id') ?? '';
  const firstName = pickString(raw, 'name', 'firstName', 'first_name') ?? '';
  const lastName = pickString(raw, 'surName', 'lastName', 'last_name', 'surname') ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const username = pickString(raw, 'username', 'userName', 'handle') ?? '';
  const displayName = fullName || pickString(raw, 'fullName', 'displayName') || username || 'User';
  const avatarUrl = pickMediaUrl(
    raw,
    'profileImageThumbnail',
    'profileImage',
    'avatarUrl',
    'profilePic',
    'photo',
  );
  const userTypeId = pickNumber(raw, 'userTypeId', 'user_type_id');
  const subtitle = getUserTypeLabel(userTypeId);

  return {
    id,
    name: displayName,
    username,
    initials: getInitials(displayName),
    verified: pickBool(raw, 'userVerified', 'isVerified', 'verified'),
    subtitle,
    avatarUrl,
    isPrivate: pickBool(raw, 'isPrivate'),
    isPremium: pickBool(raw, 'isPremiumUser', 'isPremium'),
  };
}

interface FollowListParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

function normalizeFollowList(
  payload: unknown,
  /** Which side of the follow relationship to read from each row. */
  side: 'followed' | 'follower',
): ProfileConnectionPage {
  const root = (payload ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(root.data) ? (root.data as Array<Record<string, unknown>>) : [];
  const items = rows
    .map((row) => {
      const inner = (row[side] ?? row.user ?? row) as Record<string, unknown> | undefined;
      if (!inner || typeof inner !== 'object') return null;
      return normalizeConnectionUser(inner);
    })
    .filter((entry): entry is ProfileConnection => entry !== null && Boolean(entry.id));

  const totalCount = pickNumber(root, 'totalCount', 'total', 'count') || items.length;
  return { items, totalCount };
}

/** GET /userservice/api/v3/user/follow?followerUserId=:userId — list of users :userId is following. */
export async function fetchFollowingList(
  userId: string | number,
  params: FollowListParams = {},
): Promise<ProfileConnectionPage> {
  const { page = 0, size = 10, sortBy = 'id', sortOrder = 'DESC' } = params;
  const { data } = await api.get<unknown>(servicePath('user', 'user/follow'), {
    params: { followerUserId: userId, page, size, sortBy, sortOrder },
  });
  return normalizeFollowList(data, 'followed');
}

/** GET /userservice/api/v3/user/follow?followedUserId=:userId — list of users following :userId. */
export async function fetchFollowersList(
  userId: string | number,
  params: FollowListParams = {},
): Promise<ProfileConnectionPage> {
  const { page = 0, size = 10, sortBy = 'id', sortOrder = 'DESC' } = params;
  const { data } = await api.get<unknown>(servicePath('user', 'user/follow'), {
    params: { followedUserId: userId, page, size, sortBy, sortOrder },
  });
  return normalizeFollowList(data, 'follower');
}

// ---------------------------------------------------------------------------
// GET /midl/api/v3/board/joined?userId=:userId — classes the user has joined.
// ---------------------------------------------------------------------------

export interface JoinedClassesPage {
  readonly items: readonly ProfileJoinedClassListItem[];
  readonly totalCount: number;
}

// Stable palette for class dots, indexed by board id so a given class keeps
// the same colour across renders.
const CLASS_DOT_PALETTE = [
  'bg-[#60a5fa]', // blue
  'bg-[#fb923c]', // orange
  'bg-[#34d399]', // green
  'bg-[#f472b6]', // pink
  'bg-[#a78bfa]', // purple
  'bg-[#facc15]', // yellow
  'bg-[#f87171]', // red
  'bg-[#22d3ee]', // cyan
] as const;

function dotClassForId(id: number | string): string {
  const numeric = typeof id === 'number' ? id : Number(id);
  const safe = Number.isFinite(numeric) ? Math.abs(numeric) : 0;
  return CLASS_DOT_PALETTE[safe % CLASS_DOT_PALETTE.length];
}

function normalizeJoinedClassRow(row: Record<string, unknown>): ProfileJoinedClassListItem | null {
  const board = (row.board ?? row) as Record<string, unknown>;
  if (!board || typeof board !== 'object') return null;

  const id = pickString(board, 'id', 'boardId', '_id') ?? '';
  const code = pickString(board, 'code') ?? '';
  const name = pickString(board, 'name', 'title') ?? '';
  if (!id || !code) return null;

  // Make the board resolvable by id OR by code from the URL bar — required
  // for hard-refreshes of `/class/<boardId>/...` and any internal links that
  // still navigate by class code.
  registerBoard({
    id,
    code,
    name: name || code,
    isPrivate: pickBool(board, 'isPrivate'),
  });

  return {
    id,
    code,
    name: name || code,
    memberCount: pickNumber(board, 'joinBoardCount', 'memberCount', 'membersCount'),
    isPrivate: pickBool(board, 'isPrivate'),
    dotClassName: dotClassForId(id),
  };
}

/** GET /midl/api/v3/board/joined?userId=:userId — classes the user has joined. */
export async function fetchJoinedClasses(
  userId: string | number,
  params: { page?: number; size?: number; sortBy?: string; sortOrder?: 'ASC' | 'DESC' } = {},
): Promise<JoinedClassesPage> {
  const { page = 0, size = 10, sortBy = 'id', sortOrder = 'DESC' } = params;
  const { data } = await api.get<unknown>(servicePath('midl', 'board/joined'), {
    params: { userId, page, size, sortBy, sortOrder },
  });
  const root = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(root.data) ? (root.data as Array<Record<string, unknown>>) : [];
  const items = rows
    .map((row) => normalizeJoinedClassRow(row))
    .filter((entry): entry is ProfileJoinedClassListItem => entry !== null);
  const totalCount = pickNumber(root, 'totalCount', 'total', 'count') || items.length;
  return { items, totalCount };
}

// ---------------------------------------------------------------------------
// POST /mediaservice/api/v3/media — binary image upload.
//   ?binaryMode=true
//   &mediaName=<filename>
//   &objectType=user
//   &mediaFor=UserBackground | UserProfile
//   &mediaType=Image
//   &objectDataId=<userId>
//
// Body: raw binary (Blob/ArrayBuffer) with `Content-Type: application/octet-stream`.
// ---------------------------------------------------------------------------

export type UserMediaFor = 'UserBackground' | 'UserProfile';

export interface UploadUserMediaOptions {
  /** Authenticated user id — sent as `objectDataId`. */
  userId: string | number;
  /** What this asset represents — `UserBackground` for the cover banner, `UserProfile` for the avatar. */
  mediaFor: UserMediaFor;
  /** Optional explicit filename. Defaults to a timestamped `.png`/`.jpg` based on the blob mime. */
  mediaName?: string;
  /** Image payload — `Blob` (preferred) or `File`. */
  blob: Blob;
}

export interface UploadUserMediaResult {
  /** Best-guess public URL for the uploaded asset (mediaPath / compressedMediaPath / thumbnailPath). */
  url?: string;
  /** Raw response body from the mediaservice for callers that need extra fields. */
  raw: unknown;
}

function defaultMediaName(blob: Blob): string {
  const ext =
    blob.type === 'image/png'
      ? 'png'
      : blob.type === 'image/webp'
        ? 'webp'
        : blob.type === 'image/gif'
          ? 'gif'
          : 'jpg';
  return `${Date.now()}.${ext}`;
}

function extractMediaUrl(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const root = payload as Record<string, unknown>;
  const candidates: Array<Record<string, unknown> | undefined> = [
    root,
    root.data as Record<string, unknown> | undefined,
    root.result as Record<string, unknown> | undefined,
    root.media as Record<string, unknown> | undefined,
  ];
  for (const node of candidates) {
    if (!node || typeof node !== 'object') continue;
    const direct = pickMediaUrl(
      node,
      'mediaPath',
      'compressedMediaPath',
      'thumbnailPath',
      'url',
      'path',
    );
    if (direct) return direct;
  }
  return undefined;
}

/**
 * POST /mediaservice/api/v3/media — upload a raw image blob for a user-scoped asset.
 *
 * @example
 *   await uploadUserMedia({
 *     userId: 6560879,
 *     mediaFor: 'UserBackground',
 *     blob: croppedBlob,
 *   });
 */
export async function uploadUserMedia(
  options: UploadUserMediaOptions,
): Promise<UploadUserMediaResult> {
  const { userId, mediaFor, blob } = options;
  const mediaName = options.mediaName ?? defaultMediaName(blob);

  const { data } = await api.post<unknown>(servicePath('media', 'media'), blob, {
    params: {
      binaryMode: true,
      mediaName,
      objectType: 'user',
      mediaFor,
      mediaType: 'Image',
      objectDataId: userId,
    },
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    // Don't let axios try to JSON-serialize the blob.
    transformRequest: [(body) => body],
  });

  return { url: extractMediaUrl(data), raw: data };
}

/** Convenience wrapper for the profile background / cover image. */
export function uploadProfileBackground(
  userId: string | number,
  blob: Blob,
  mediaName?: string,
): Promise<UploadUserMediaResult> {
  return uploadUserMedia({ userId, mediaFor: 'UserBackground', blob, mediaName });
}

/** Convenience wrapper for the profile avatar. */
export function uploadProfileAvatar(
  userId: string | number,
  blob: Blob,
  mediaName?: string,
): Promise<UploadUserMediaResult> {
  return uploadUserMedia({ userId, mediaFor: 'UserProfile', blob, mediaName });
}

// ---------------------------------------------------------------------------
// POST /userservice/api/v3/user/changePassword
// ---------------------------------------------------------------------------

export interface ChangePasswordPayload {
  userId: number;
  /** Current password. */
  password: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * POST /userservice/api/v3/user/changePassword — update the logged-in user's password.
 *
 * The MyU backend expects the current password as `password` and the new
 * password mirrored in both `newPassword` and `confirmPassword`.
 */
export async function changeUserPassword(payload: ChangePasswordPayload): Promise<void> {
  await api.post<unknown>(servicePath('user', 'user/changePassword'), payload);
}
